import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { CheckCircleIcon, DevicePhoneMobileIcon } from "@heroicons/react/24/outline";
import { functions } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";

/**
 * Completes the Wear OS device-flow pairing.
 *
 * Flow:
 *   1. The user opens this page (either via QR deep-link from their watch,
 *      `peak.app/link?code=ABCD1234`, or by manually typing it in).
 *   2. We auto-submit if a valid-looking code is in the URL; otherwise the
 *      user types it into the input below.
 *   3. We call the `completeWatchPairing` cloud function, which mints a
 *      Firebase custom auth token for the signed-in user and stores it in
 *      `watchPairings/{code}` with a 5-minute TTL.
 *   4. The watch — which polls `pollWatchPairing` every 2s with the code and
 *      a secret key it registered when it showed the code — picks up the
 *      token and signs in. The code alone cannot collect the token.
 */
const CODE_REGEX = /^[A-Z0-9]{4,12}$/;
const ALPHABET_HINT = /[^A-Z0-9]/g;

type Status =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "done" }
  | { kind: "error"; message: string };

const PairWatch: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const initialCode = useMemo(() => {
    const raw = new URLSearchParams(location.search).get("code") ?? "";
    return raw.toUpperCase().replace(ALPHABET_HINT, "").slice(0, 12);
  }, [location.search]);

  const [code, setCode] = useState(initialCode);
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  // Auto-submit when we arrive with a well-formed code in the URL; the user
  // is typically coming from a QR scan on their watch and shouldn't have to
  // tap a confirmation button.
  useEffect(() => {
    if (!currentUser) return;
    if (status.kind !== "idle") return;
    if (!initialCode || !CODE_REGEX.test(initialCode)) return;
    void submit(initialCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, initialCode]);

  const submit = async (raw: string) => {
    const trimmed = raw.trim().toUpperCase();
    if (!CODE_REGEX.test(trimmed)) {
      setStatus({ kind: "error", message: "That code doesn't look right." });
      return;
    }
    setStatus({ kind: "sending" });
    try {
      const callable = httpsCallable<{ code: string }, { ok: boolean }>(
        functions,
        "completeWatchPairing",
      );
      await callable({ code: trimmed });
      setStatus({ kind: "done" });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Pairing failed. Please try again.";
      setStatus({ kind: "error", message });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submit(code);
  };

  const handleChange = (v: string) => {
    setCode(v.toUpperCase().replace(ALPHABET_HINT, "").slice(0, 12));
    if (status.kind === "error") setStatus({ kind: "idle" });
  };

  return (
    <div className="min-h-screen bg-base-100 text-base-content">
      <div className="max-w-md mx-auto px-6 pt-12 pb-12 flex flex-col gap-6">
        <div className="flex items-start gap-3">
          <DevicePhoneMobileIcon className="h-8 w-8 text-primary flex-shrink-0 mt-0.5" />
          <div className="space-y-1.5">
            <h2 className="text-xl font-grotesk font-semibold">
              Link your Peak watch app
            </h2>
            <p className="text-sm text-base-content/70 leading-relaxed">
              Open the Peak app on your Wear OS watch. It will display a
              pairing code — either scan the QR with your phone camera, or
              type it in below.
            </p>
          </div>
        </div>

        {status.kind === "done" ? (
          <SuccessCard onDone={() => navigate("/feed")} />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-base-content/50 font-grotesk">
                Pairing code
              </span>
              <input
                type="text"
                autoFocus
                inputMode="text"
                autoComplete="off"
                autoCapitalize="characters"
                className="mt-1 w-full rounded-lg border border-base-300 bg-base-200 px-4 py-3 text-2xl font-mono tracking-[0.3em] tabular-nums uppercase text-center focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="ABCD1234"
                value={code}
                onChange={(e) => handleChange(e.target.value)}
                disabled={status.kind === "sending"}
              />
            </label>

            {status.kind === "error" && (
              <p className="text-sm text-error" role="alert">
                {status.message}
              </p>
            )}

            <button
              type="submit"
              className="w-full rounded-lg bg-primary text-primary-content px-4 py-3 font-grotesk font-semibold hover:opacity-90 disabled:opacity-60"
              disabled={status.kind === "sending" || !CODE_REGEX.test(code)}
            >
              {status.kind === "sending" ? "Pairing…" : "Pair watch"}
            </button>

            <p className="text-xs text-base-content/50 leading-relaxed">
              Codes expire after five minutes. If yours has expired, just tap
              the pair screen on your watch to get a fresh one.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

const SuccessCard: React.FC<{ onDone: () => void }> = ({ onDone }) => (
  <div className="rounded-lg border border-green-500/30 bg-green-500/5 p-4 flex flex-col gap-3">
    <div className="flex items-center gap-2">
      <CheckCircleIcon className="h-5 w-5 text-green-500" />
      <h3 className="font-grotesk font-semibold">Watch paired</h3>
    </div>
    <p className="text-sm text-base-content/70">
      Your watch should sign in within a couple of seconds. You can now close
      this page.
    </p>
    <button
      type="button"
      onClick={onDone}
      className="self-start rounded-md bg-base-200 hover:bg-base-300 px-3 py-1.5 text-sm font-grotesk"
    >
      Back to feed
    </button>
  </div>
);

export default PairWatch;
